import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'
import { useIdempotentSend } from '@/lib/idempotent-send'
import { invalidateInventory } from '@/lib/inventory-invalidation'

export {
  type CategoryOption,
  type CollectionOption,
  useScopeCategories,
  useScopeCollections,
} from './count-scope-api'

/**
 * The stock count API (WP01 step 11). What a sheet adds up to — counted,
 * uncounted, the variance and its value — and what the reader may do next
 * are the server's (`summary`, `abilities`); nothing here derives a rule.
 * Keys live under ['inventory', company, 'count-sessions'], the family the
 * invalidation map owns.
 */

export type CountSession = components['schemas']['StockCountSessionResource']
export type CountLine = components['schemas']['StockCountLineResource']
export type CountStatus = 'open' | 'submitted' | 'approved' | 'rejected' | 'applied' | 'cancelled'

export const COUNT_STATUSES: CountStatus[] = [
  'open',
  'submitted',
  'approved',
  'applied',
  'rejected',
  'cancelled',
]

/** A count still moving: someone else may count, submit or decide it while it is on screen. */
export const isActiveCount = (status: string | undefined) =>
  status === 'open' || status === 'submitted'

export type CountFilters = {
  status?: CountStatus
  warehouse?: string
  mine?: boolean
  q?: string
  page?: number
  perPage?: number
}

export const countKeys = {
  all: (companyId: string | null) => ['inventory', companyId, 'count-sessions'] as const,
  list: (companyId: string | null, filters: CountFilters, userId: string | null) =>
    ['inventory', companyId, 'count-sessions', 'list', filters, userId] as const,
  detail: (companyId: string | null, sessionId: string) =>
    ['inventory', companyId, 'count-sessions', 'detail', sessionId] as const,
}

const headers = (companyId: string, idempotencyKey?: string) => ({
  'X-Company-Id': companyId,
  ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
})

function expectSession(body: { data: CountSession } | undefined): CountSession {
  if (!body) throw new Error('The count endpoint returned no body')
  return body.data
}

export function useCounts(companyId: string | null, filters: CountFilters, userId: string | null) {
  return useQuery({
    queryKey: countKeys.list(companyId, filters, filters.mine ? userId : null),
    enabled: Boolean(companyId),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.GET('/inventory/count-sessions', {
        headers: headers(companyId ?? ''),
        params: {
          query: {
            status: filters.status,
            warehouse_id: filters.warehouse,
            counted_by_user_id: filters.mine && userId ? userId : undefined,
            q: filters.q,
            page: filters.page,
            per_page: filters.perPage ?? 25,
          },
        },
      })
      if (!response.data) throw new Error('GET /inventory/count-sessions returned no body')
      return response.data
    },
  })
}

/**
 * One count sheet. While it is open or submitted it polls, so another
 * counter's lines and an approver's decision show up without a reload — but
 * not while the reader is typing in a cell (`paused`), so a refetch never
 * lands under their cursor.
 */
export function useCount(companyId: string | null, sessionId: string, paused = false) {
  return useQuery({
    queryKey: countKeys.detail(companyId, sessionId),
    enabled: Boolean(companyId),
    refetchInterval: (query) =>
      !paused && isActiveCount(query.state.data?.status) ? 30_000 : false,
    refetchOnWindowFocus: (query) => !paused && isActiveCount(query.state.data?.status),
    queryFn: async (): Promise<CountSession> => {
      const response = await api.GET('/inventory/count-sessions/{countSession}', {
        headers: headers(companyId ?? ''),
        params: { path: { countSession: sessionId } },
      })
      return expectSession(response.data)
    },
  })
}

export type OpenCountBody = components['schemas']['StockCountSessionStoreRequest']

/**
 * Open a count and fill its sheet from stock in one go: one uncounted line
 * per balance in scope. Generation is idempotent, so a retry after a failed
 * second call adds only what is missing.
 */
export function useOpenCount(companyId: string | null) {
  const queryClient = useQueryClient()
  const send = useIdempotentSend()
  return useMutation({
    mutationFn: async (input: { body: OpenCountBody }) =>
      send(input, async (key) => {
        const opened = await api.POST('/inventory/count-sessions', {
          headers: headers(companyId ?? '', key),
          body: input.body,
        })
        const session = expectSession(opened.data)
        const generated = await api.POST(
          '/inventory/count-sessions/{countSession}/lines/generation',
          {
            headers: headers(companyId ?? '', `${key}:lines`),
            params: { path: { countSession: session.id } },
          },
        )
        return expectSession(generated.data)
      }),
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'count.line'),
  })
}

/**
 * Record, change or take back one line's count. `null` is "not counted";
 * `0` is "counted, and the shelf is empty" — they are never the same.
 */
export function useSaveCountLine(companyId: string | null, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      lineId: string
      counted: number | null
      reason: string | null
    }): Promise<CountLine> => {
      const response = await api.PATCH(
        '/inventory/count-sessions/{countSession}/lines/{countLine}',
        {
          headers: headers(companyId ?? ''),
          params: { path: { countSession: sessionId, countLine: input.lineId } },
          body: { counted_quantity: input.counted, reason: input.reason },
        },
      )
      if (!response.data) throw new Error('The count line endpoint returned no body')
      return response.data.data
    },
    onSuccess: (line) => {
      // The row updates at once; the summary follows from the refetch.
      queryClient.setQueryData<CountSession>(countKeys.detail(companyId, sessionId), (current) =>
        current
          ? {
              ...current,
              lines: current.lines.map((entry) => (entry.id === line.id ? line : entry)),
            }
          : current,
      )
      void invalidateInventory(queryClient, companyId, 'count.line')
    },
  })
}

/**
 * Takes a line off an open sheet (WP03). The answer is the whole sheet, so
 * the rows and the summary update at once, without waiting for a refetch.
 */
export function useRemoveCountLine(companyId: string | null, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (lineId: string): Promise<CountSession> => {
      const response = await api.DELETE(
        '/inventory/count-sessions/{countSession}/lines/{countLine}',
        {
          headers: headers(companyId ?? ''),
          params: { path: { countSession: sessionId, countLine: lineId } },
        },
      )
      if (!response.data) throw new Error('The count line endpoint returned no body')
      return response.data.data
    },
    onSuccess: (session) => {
      queryClient.setQueryData<CountSession>(countKeys.detail(companyId, sessionId), session)
      void invalidateInventory(queryClient, companyId, 'count.line')
    },
  })
}

export type AddCountLineBody = components['schemas']['StockCountLineStoreRequest']

/** A line the generator did not add: stock found where the system holds none. */
export function useAddCountLine(companyId: string | null, sessionId: string) {
  const queryClient = useQueryClient()
  const send = useIdempotentSend()
  return useMutation({
    mutationFn: async (input: { body: AddCountLineBody }) =>
      send(input, async (key) => {
        const response = await api.POST('/inventory/count-sessions/{countSession}/lines', {
          headers: headers(companyId ?? '', key),
          params: { path: { countSession: sessionId } },
          body: input.body,
        })
        if (!response.data) throw new Error('The count line endpoint returned no body')
        return response.data.data
      }),
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'count.line'),
  })
}

/** A transition with no body: submit for approval, or cancel. */
export function useCountStep(companyId: string | null, step: 'submissions' | 'cancellations') {
  const queryClient = useQueryClient()
  const send = useIdempotentSend()
  return useMutation({
    mutationFn: async (input: { sessionId: string }) =>
      send(input, async (key) => {
        const init = {
          headers: headers(companyId ?? '', key),
          params: { path: { countSession: input.sessionId } },
        }
        const response =
          step === 'submissions'
            ? await api.POST('/inventory/count-sessions/{countSession}/submissions', init)
            : await api.POST('/inventory/count-sessions/{countSession}/cancellations', init)
        return expectSession(response.data)
      }),
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'count.decided'),
  })
}

/** Approving also applies the count: stock moves and the ledger posts, so the stock family refreshes. */
export function useDecideCount(companyId: string | null) {
  const queryClient = useQueryClient()
  const send = useIdempotentSend()
  return useMutation({
    mutationFn: async (input: {
      sessionId: string
      decision: 'approved' | 'rejected'
      comment: string
    }) =>
      send(input, async (key) => {
        const response = await api.POST('/inventory/count-sessions/{countSession}/approvals', {
          headers: headers(companyId ?? '', key),
          params: { path: { countSession: input.sessionId } },
          body: { decision: input.decision, comment: input.comment.trim() || null },
        })
        return expectSession(response.data)
      }),
    onSuccess: (_session, input) =>
      void invalidateInventory(
        queryClient,
        companyId,
        input.decision === 'approved' ? 'count.applied' : 'count.decided',
      ),
  })
}

/** The server's discrepancy report, as the file it renders in the reader's language. */
export function exportDiscrepancies(
  companyId: string,
  session: Pick<CountSession, 'id' | 'reference'>,
  format: 'csv' | 'pdf',
): Promise<void> {
  const url = `${apiOrigin}/api/inventory/count-sessions/${encodeURIComponent(session.id)}/discrepancies?format=${format}`
  return downloadFile(url, `${session.reference}-discrepancies.${format}`, {
    'X-Company-Id': companyId,
  })
}
